import { useMemo } from "react";
import { MdInfoOutline } from "react-icons/md";
import clsx from "clsx";
import ButtonGroup from "../ButtonGroup/ButtonGroup";
import useMobile from "src/hooks/useMobile";
import { forceIconSize } from "src/helpers/icon-utils";
import { ICON_SIZES } from "src/constants/icons";
import { EInfoBlockKind, IInfoBlock } from "./InfoBlock.types";

const InfoBlock: React.FC<IInfoBlock> = ({
  icon = <MdInfoOutline size={ICON_SIZES.LG} />,
  title,
  message,
  buttons = null,
  displayMobileIcon = false,
  kind = EInfoBlockKind.INFO,
  mobileButtonsPosition = "right",
  desktopButtonsPosition = "right",
  iconPosition = "center",
  iconInTitle = false,
  important = true,
}) => {
  const { isMobile } = useMobile();

  const autoIconSize = useMemo(() => {
    if (iconInTitle) {
      return ICON_SIZES.XS;
    }

    return ICON_SIZES.LG;
  }, [iconInTitle]);

  const bg = useMemo(() => {
    switch (kind) {
      case EInfoBlockKind.WARNING:
        return clsx([
          "bg-yellow-50",
          "dark:bg-yellow-100",
          "dark:text-yellow-900",
          "border-yellow-200",
          "dark:border-yellow-950",
        ]);
      case EInfoBlockKind.ERROR:
        return clsx([
          "bg-red-50",
          "dark:bg-red-100",
          "dark:text-red-900",
          "border-red-200",
          "dark:border-red-950",
        ]);
      case EInfoBlockKind.SUCCESS:
        return clsx([
          "bg-green-50",
          "dark:bg-green-300",
          "dark:text-green-900",
          "border-green-200",
          "dark:border-green-950",
        ]);
      default:
        return clsx([
          "bg-blue-50",
          "dark:bg-sky-200",
          "dark:text-sky-900",
          "border-blue-200",
          "dark:border-blue-900",
        ]);
    }
  }, [kind]);

  const iconColor = useMemo(() => {
    switch (kind) {
      case EInfoBlockKind.WARNING:
        return "text-yellow-500 dark:text-yellow-600";
      case EInfoBlockKind.ERROR:
        return "text-red-500 dark:text-red-600";
      case EInfoBlockKind.SUCCESS:
        return "text-green-600 dark:text-green-600";
      default:
        return "text-blue-500 dark:text-sky-600";
    }
  }, [kind]);

  const importantColor = useMemo(() => {
    switch (kind) {
      case EInfoBlockKind.WARNING:
        return "bg-yellow-500";
      case EInfoBlockKind.ERROR:
        return "bg-red-500";
      case EInfoBlockKind.SUCCESS:
        return "bg-green-500";
      default:
        return "bg-blue-400";
    }
  }, [kind, important]);

  return (
    <div
      className={clsx([
        "flex",
        "items-center",
        "justify-center",
        "w-full",
        {
          shadow: important,
        },
      ])}
    >
      <div
        className={clsx([
          "w-full",
          "flex",
          "flex-row",
          "md:flex-row",
          "rounded",
          "md:rounded",
          "items-start",
          "md:items-center",
          "md:gap-2",
          "gap-1",
          "p-2",
          "md:p-2",
          bg,
          "relative",
          "dark:text-black",
          "border",
          "relative",
        ])}
      >
        {icon && !iconInTitle ? (
          <div
            className={clsx([
              {
                hidden: !displayMobileIcon,
                "self-center": iconPosition === "center",
                "self-start": iconPosition === "top",
                // "animate-pulse": important,
              },
              "md:flex",
              iconColor,
            ])}
          >
            {forceIconSize({ icon, size: autoIconSize })}
          </div>
        ) : null}
        <div
          className={clsx([
            "flex",
            "items-center",
            "gap-3",
            "md:gap-3",
            {
              "flex-col": mobileButtonsPosition === "bottom",
              "flex-row": mobileButtonsPosition === "right",
              "lg:flex-row": desktopButtonsPosition === "right",
              "lg:flex-col": desktopButtonsPosition === "bottom",
              "md:gap-3": desktopButtonsPosition === "bottom",
              "md:gap-1": desktopButtonsPosition === "right",
            },
            "w-full",
            "justify-between",
            "z-0",
            "self-center", //added
          ])}
        >
          {important && icon ? (
            <div
              className={clsx([
                "absolute",
                "w-[2px]",
                "top-3",
                "bottom-3",
                "md:top-4",
                "md:bottom-4",
                "rounded-full",
                "opacity-50",
                importantColor,
                {
                  hidden: isMobile && mobileButtonsPosition === "bottom",
                },
              ])}
            />
          ) : null}
          <div
            className={clsx([
              "flex",
              "flex-col",
              "w-full",
              "flex-grow",
              "flex-shrink",
              {
                "pl-3": important && icon,
              },
            ])}
          >
            {title && (
              <h5
                className={clsx([
                  "font-medium",
                  "text-xs",
                  "md:text-xs",
                  "flex",
                  "flex-row",
                  "gap-1",
                ])}
              >
                {iconInTitle && icon ? (
                  <div
                    className={clsx([
                      {
                        hidden: !displayMobileIcon,
                        "self-center": iconPosition === "center",
                        "self-start": iconPosition === "top",
                      },
                      "md:flex",
                    ])}
                  >
                    {forceIconSize({ icon, size: ICON_SIZES.XS })}
                  </div>
                ) : null}
                {title}
              </h5>
            )}
            <div
              className={clsx([
                "text-xs",
                "md:text-xs",
                "text-left",
                "flex",
                "w-full",
              ])}
            >
              {Array.isArray(message) ? (
                <ul className={clsx(["list-disc", "list-inside"])}>
                  {message.map((messageLine, index) => {
                    return <li key={index}>{messageLine}</li>;
                  })}
                </ul>
              ) : (
                message
              )}
            </div>
          </div>
          {buttons && (
            <div
              className={clsx([
                "flex",
                "flex-shrink-0",
                "justify-end",
                {
                  "w-full": mobileButtonsPosition === "bottom" && isMobile,
                  "w-auto": mobileButtonsPosition === "right",
                  "md:w-full": desktopButtonsPosition === "bottom",
                  "md:w-auto": mobileButtonsPosition === "right",
                },
              ])}
            >
              <ButtonGroup buttons={buttons} gap={1} align="right" />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default InfoBlock;
